/**
 * Emits `<meta name="robots" content="noindex,nofollow">` on the routes that
 * should never appear in a search index. Registered as an appExtra, so it
 * mounts on every page and decides from the pathname.
 *
 * WHY THIS EXISTS
 *
 * Core's Login component emits exactly one head tag — `<Helmet title="Login" />`
 * — and nothing else. So `<path>/login` answered 200 with a bare
 * `<title>Login</title>`, fully crawlable. Combined with the 302 that used to
 * send anonymous visitors there (see UnauthorizedView), Google indexed login
 * pages under content URLs and served them as results.
 *
 * `robots.txt` does have `Disallow: /login`, but that is a path PREFIX: it
 * matches `/login` and `/login/…` and does NOT match `/news/<slug>/login`,
 * which is the form Volto actually produces. The robots.txt rule is being
 * widened for Googlebot in the same change to disallow a wildcard first
 * segment followed by /login (see backend/scripts/seo/set_robots.py — the
 * literal pattern is not written here because it contains the character pair
 * that ends a block comment). But a robots.txt rule only stops crawling — it does not remove a URL already
 * indexed, and Google may still list a disallowed URL it has seen linked. A
 * meta robots tag on a page Google is allowed to fetch is the instruction that
 * actually de-indexes. Belt and braces, and they do different jobs.
 *
 * This is an appExtra rather than a shadow of core's Login for a simple
 * reason: shadowing means copying ~200 lines of a component we do not want to
 * change, and inheriting the maintenance of it at every Volto upgrade. One
 * matcher covers login, logout, registration, password flows and the CMS
 * action routes at once.
 *
 * Registered with `match: ''` (every route) because the decision is made here,
 * from the LAST path segment, not by react-router pattern matching — the
 * segments below can appear at any depth in seamless mode.
 */
import React from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';

import { shouldNoIndexType } from './noIndexTypes';

/**
 * Final-segment names that mark a route as non-content.
 *
 * Auth routes are the ones this change is actually about. The CMS action
 * routes are included because they are the same class of thing and are equally
 * pointless in an index — several already have robots.txt rules, which this
 * makes redundant rather than conflicting.
 */
const NOINDEX_SEGMENTS = new Set([
  'login',
  'logout',
  'register',
  'password-reset',
  'change-password',
  'add',
  'edit',
  'delete',
  'history',
  'sharing',
  'contents',
  'controlpanel',
  'layout',
  'aliases',
  'rules',
]);

export const shouldNoIndex = (pathname) => {
  if (!pathname) return false;
  const segments = pathname.split('?')[0].split('/').filter(Boolean);
  if (segments.length === 0) return false;
  const last = segments[segments.length - 1];
  // `@@`-prefixed views are Plone/Volto machinery at any depth.
  if (segments.some((s) => s.startsWith('@@'))) return true;
  return NOINDEX_SEGMENTS.has(last);
};

/**
 * Two independent reasons to withhold a page: what route it is, and what type
 * it is. The type list and the evidence behind it live in ./noIndexTypes.js,
 * shared with the sitemap so the two can never disagree.
 */
const NoIndexRoutes = () => {
  const location = useLocation();
  const portalType = useSelector((state) => state.content?.data?.['@type']);
  // Route rules win: a `/glossary/<term>/edit` is CMS machinery first.
  if (shouldNoIndex(location?.pathname)) {
    return (
      <Helmet>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
    );
  }
  if (shouldNoIndexType(portalType)) {
    return (
      <Helmet>
        <meta name="robots" content="noindex,follow" />
      </Helmet>
    );
  }
  return null;
};

export { shouldNoIndexType };
export default NoIndexRoutes;
